import React, { useState, useEffect } from 'react';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription } from '../components/ui/alert-dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Mail, Send, X, Loader2, Eye, Edit2, CheckCircle2, AlertTriangle, Link2, Copy
} from 'lucide-react';
import { toast } from 'sonner';
import { sendInterviewEmail } from '../services/api';

const DEFAULT_TEMPLATE = (candidateName, jobTitle) => `Hi ${candidateName || '[Candidate Name]'},

Thank you for your interest in the ${jobTitle || '[Job Title]'} role. As the next step, we'd like you to complete a short AI-powered video interview at your convenience.

Your AI Interview Link: Click here

What to Expect
- The interview has 8 questions (technical, work setup, and culture fit).
- An AI voice will read each question aloud, then recording starts automatically.
- Your video and audio are recorded throughout.
- The session takes approximately 15–20 minutes.
- You'll receive your results once the interview is reviewed.

Before You Begin
- Use Google Chrome for the best experience. Safari and Firefox are supported but will require you to type your answers instead of speaking.
- Ensure your camera and microphone are working — you'll be asked to grant access.
- Find a quiet, well-lit space with a stable internet connection.
- Keep your ID or resume handy in case you need to reference details.
- Close unnecessary browser tabs and applications.

During the Interview
- Speak clearly into your microphone. A live transcript will appear on screen as you talk.
- If voice input isn't detected, a text box will appear — simply type your answer instead.
- Click "Stop & Submit" when you've finished answering, or the timer will auto-submit for you.
- Each question has a time limit displayed on screen. Pace yourself accordingly.

Important Rules
- Do not switch tabs or leave the interview window. Tab switches are detected and recorded as violations — this will be visible in your report.
- Do not copy or paste text during the interview.
- Complete the interview in one sitting. You cannot pause and return later.
- Be yourself. The AI evaluates relevance, clarity, and depth of your answers — not perfection.

If you face any technical issues, please reply to this email and we'll assist you promptly.

We look forward to hearing from you.

Good luck!

Warm regards,
HireFast AI Team`;

const EmailComposeModal = ({
  open,
  onClose,
  candidateName,
  candidateEmail,
  jobTitle,
  jobId,
  candidateId,
  interviewSessionId,
  interviewUrl,
  region
}) => {
  const [toEmail, setToEmail] = useState('');
  const [ccEmail, setCcEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [isPreview, setIsPreview] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    if (open) {
      setToEmail(candidateEmail || '');
      setCcEmail('');
      setSubject(`AI Video Interview – Instructions & Guidelines | ${jobTitle || 'Open Position'}`);
      setBody(DEFAULT_TEMPLATE(candidateName, jobTitle));
      setSent(false);
      setIsPreview(false);
    }
  }, [open, candidateEmail, candidateName, jobTitle]);

  const handleSend = async () => {
    if (!toEmail.trim()) {
      toast.error('Please enter a recipient email address');
      return;
    }
    setSending(true);
    try {
      await sendInterviewEmail({
        to_email: toEmail.trim(),
        cc_email: ccEmail.trim() || null,
        subject,
        body,
        job_id: jobId,
        candidate_id: candidateId,
        interview_session_id: interviewSessionId,
        interview_url: interviewUrl,
        region: region || 'global'
      });
      setSent(true);
      toast.success('Interview invitation email sent successfully!');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to send email';
      toast.error(detail);
      if (detail.includes('not configured')) {
        toast.info('Go to Settings → Email Configuration to set up SMTP');
      }
    } finally {
      setSending(false);
    }
  };

  const handleCopyLink = () => {
    if (interviewUrl) {
      navigator.clipboard.writeText(interviewUrl);
      setLinkCopied(true);
      toast.success('Interview link copied!');
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  const renderPreview = () => {
    const lines = body.split('\n');
    const linkText = 'Click here';
    return (
      <div className="border rounded-lg overflow-hidden">
        <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-4 text-center">
          <h2 className="text-white text-lg font-bold tracking-wide">HireFast AI</h2>
          <p className="text-slate-400 text-[10px] tracking-widest mt-0.5">POWERED BY TEKWISSEN</p>
        </div>
        <div className="p-6 text-sm leading-relaxed bg-white text-slate-800">
          {lines.map((line, i) => (
            <React.Fragment key={i}>
              {line.includes(linkText) ? (
                <>
                  {line.split(linkText)[0]}
                  <a href={interviewUrl || '#'} style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'underline' }}>{linkText}</a>
                  {line.split(linkText).slice(1).join(linkText)}
                </>
              ) : (
                line
              )}
              {i < lines.length - 1 && <br />}
            </React.Fragment>
          ))}
        </div>
        <div className="bg-slate-50 border-t px-6 py-3">
          <p className="text-[10px] text-slate-500 text-center">
            This is an automated email from HireFast AI Interview Platform.
          </p>
        </div>
      </div>
    );
  };

  return (
    <AlertDialog open={open} onOpenChange={(v) => !v && onClose()}>
      <AlertDialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="email-compose-modal">
        <AlertDialogHeader>
          <div className="flex items-center justify-between">
            <AlertDialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-primary" />
              {sent ? 'Email Sent' : 'Send Interview Invitation'}
            </AlertDialogTitle>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <AlertDialogDescription className="sr-only">
            Compose and send interview invitation email
          </AlertDialogDescription>
        </AlertDialogHeader>

        {sent ? (
          /* Success state */
          <div className="text-center py-8 space-y-4" data-testid="email-sent-success">
            <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto" />
            <div>
              <p className="text-lg font-semibold">Email Sent Successfully!</p>
              <p className="text-sm text-muted-foreground mt-1">
                Interview invitation sent to <strong>{toEmail}</strong>
              </p>
            </div>
            {interviewUrl && (
              <div className="flex items-center gap-2 justify-center mt-4">
                <Badge variant="outline" className="text-xs gap-1 px-3 py-1.5">
                  <Link2 className="h-3 w-3" />
                  Interview link active
                </Badge>
              </div>
            )}
            <Button onClick={onClose} className="mt-4" data-testid="email-done-btn">Done</Button>
          </div>
        ) : (
          /* Compose form */
          <div className="space-y-4 mt-2">
            {/* Interview Link Info */}
            {interviewUrl && (
              <div className="p-3 bg-blue-500/5 border border-blue-500/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Link2 className="h-4 w-4 text-blue-500" />
                    <span className="text-xs font-medium">Interview Link</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs gap-1"
                    onClick={handleCopyLink}
                    data-testid="copy-link-btn"
                  >
                    {linkCopied ? <CheckCircle2 className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                    {linkCopied ? 'Copied' : 'Copy'}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1 truncate">{interviewUrl}</p>
              </div>
            )}

            {/* TO / CC */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">To (Candidate Email) *</Label>
                <Input
                  type="email"
                  value={toEmail}
                  onChange={(e) => setToEmail(e.target.value)}
                  placeholder="candidate@email.com"
                  data-testid="email-to-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">CC (Optional)</Label>
                <Input
                  type="email"
                  value={ccEmail}
                  onChange={(e) => setCcEmail(e.target.value)}
                  placeholder="yourname@company.com"
                  data-testid="email-cc-input"
                />
              </div>
            </div>

            {/* Subject */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Subject</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                data-testid="email-subject-input"
              />
            </div>

            <Separator />

            {/* Toggle between edit and preview */}
            <div className="flex items-center gap-2">
              <Button
                variant={!isPreview ? "default" : "outline"}
                size="sm"
                className="text-xs gap-1"
                onClick={() => setIsPreview(false)}
              >
                <Edit2 className="h-3 w-3" />
                Edit
              </Button>
              <Button
                variant={isPreview ? "default" : "outline"}
                size="sm"
                className="text-xs gap-1"
                onClick={() => setIsPreview(true)}
                data-testid="email-preview-btn"
              >
                <Eye className="h-3 w-3" />
                Preview
              </Button>
            </div>

            {/* Body */}
            {isPreview ? (
              <div className="max-h-[350px] overflow-y-auto">{renderPreview()}</div>
            ) : (
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="min-h-[300px] font-mono text-sm"
                placeholder="Email body..."
                data-testid="email-body-textarea"
              />
            )}

            {/* Actions */}
            <div className="flex items-center justify-between pt-2">
              <Button variant="outline" onClick={onClose} data-testid="email-skip-btn">
                Skip — Just Use Link
              </Button>
              <Button
                onClick={handleSend}
                disabled={sending || !toEmail.trim()}
                className="gap-2"
                data-testid="email-send-btn"
              >
                {sending ? (
                  <><Loader2 className="h-4 w-4 animate-spin" />Sending...</>
                ) : (
                  <><Send className="h-4 w-4" />Send Email</>
                )}
              </Button>
            </div>

            {/* Warning if no email config */}
            <p className="text-[10px] text-muted-foreground text-center">
              Email is sent via your configured SMTP (Settings → Email Configuration).
              &quot;Click here&quot; in the body will be auto-linked to the interview URL.
            </p>
          </div>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default EmailComposeModal;
